import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { t } from 'localization'

type RaffleCountdownProps = {
  targetDate: string
}

const TP = 'shared.components.Raffles.RaffleCountdown'

const computeTimeLeft = (targetDate: string) => {
  const diff = Math.max(0, new Date(targetDate).getTime() - Date.now())
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  }
}

const pad = (n: number) => String(n).padStart(2, '0')

export const RaffleCountdown = ({ targetDate }: RaffleCountdownProps) => {
  const [timeLeft, setTimeLeft] = useState<ReturnType<typeof computeTimeLeft> | null>(null)

  useEffect(() => {
    setTimeLeft(computeTimeLeft(targetDate))
    const interval = setInterval(() => {
      setTimeLeft(computeTimeLeft(targetDate))
    }, 1000)
    return () => clearInterval(interval)
  }, [targetDate])

  const labels = useMemo(() => ({
    days: t(`${TP}.days`, 'D'),
    hours: t(`${TP}.hours`, 'H'),
    minutes: t(`${TP}.minutes`, 'M'),
    seconds: t(`${TP}.seconds`, 'S'),
    separator: t(`${TP}.separator`, ' : '),
  }), [])
  const separator = labels.separator

  const allSegments = timeLeft
    ? [
        { value: timeLeft.days, label: labels.days, formatted: String(timeLeft.days) },
        { value: timeLeft.hours, label: labels.hours, formatted: pad(timeLeft.hours) },
        { value: timeLeft.minutes, label: labels.minutes, formatted: pad(timeLeft.minutes) },
        { value: timeLeft.seconds, label: labels.seconds, formatted: pad(timeLeft.seconds) },
      ]
    : [
        { value: 0, label: labels.minutes, formatted: '--' },
        { value: 0, label: labels.seconds, formatted: '--' },
      ]

  const minStartIndex = allSegments.length - 2
  let startIndex = 0
  while (startIndex < minStartIndex && allSegments[startIndex].value === 0) {
    startIndex++
  }
  const segments = timeLeft ? allSegments.slice(startIndex) : allSegments

  return (
    <Wrapper data-qa="raffle-countdown">
      {segments.map((seg, i) => (
        <span key={i}>
          {i > 0 && separator}
          <Digit>{seg.formatted}</Digit>
          <Digit>{seg.label}</Digit>
        </span>
      ))}
    </Wrapper>
  )
}

const Wrapper = styled.span`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  color: inherit;
  font-variant-numeric: tabular-nums;
`

const Digit = styled.span`
  font-weight: 500;
`
